// =============================================================================
// Admin Deals Management Page
// =============================================================================

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  MapPinIcon,
  UsersIcon,
  XMarkIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Card, Button, DealImage } from '../../components/ui';
import { adminService, DealWithStats, CreateDealInput } from '../../services/admin.service';
import { getApiError } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import type { PropertyType } from '../../types';

// =============================================================================
// Create Deal Modal
// =============================================================================

interface CreateDealModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreateDealModal({ isOpen, onClose, onCreated }: CreateDealModalProps) {
  const [formData, setFormData] = useState<CreateDealInput>({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    propertyType: 'MULTIFAMILY',
    totalEquityRaised: 0,
    isActive: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await adminService.createDeal(formData);
      await onCreated();
      onClose();
      // Reset form
      setFormData({
        name: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        propertyType: 'MULTIFAMILY',
        totalEquityRaised: 0,
        isActive: true,
      });
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const propertyTypes: PropertyType[] = [
    'MULTIFAMILY',
    'RETAIL',
    'OFFICE',
    'INDUSTRIAL',
    'MIXED_USE',
    'HOTEL',
    'SELF_STORAGE',
    'OTHER',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Add New Deal</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., Sunset Apartments"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Property Type *</label>
            <select
              value={formData.propertyType}
              onChange={(e) => setFormData({ ...formData, propertyType: e.target.value as PropertyType })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              {propertyTypes.map((type) => (
                <option key={type} value={type}>
                  {type.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="123 Main St"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                required
                maxLength={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="CA"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ZIP *</label>
              <input
                type="text"
                value={formData.zipCode}
                onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                required
                maxLength={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Equity Raised *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="text"
                inputMode="numeric"
                value={formData.totalEquityRaised ? formData.totalEquityRaised.toLocaleString('en-US') : ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, '');
                  setFormData({ ...formData, totalEquityRaised: parseFloat(value) || 0 });
                }}
                required
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="5,000,000"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Acquisition Date</label>
            <input
              type="date"
              value={formData.acquisitionDate || ''}
              onChange={(e) => setFormData({ ...formData, acquisitionDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Brief description of the property..."
            />
          </div>

          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="createIsActive"
              checked={formData.isActive ?? true}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="createIsActive" className="text-sm font-medium text-gray-700">
              Deal is Active
              <span className="block text-xs text-gray-500 font-normal">
                Inactive deals are hidden from investors
              </span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Deal'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

export function DealsPage() {
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canCreate = hasPermission('deals', 'create');
  const [isLoading, setIsLoading] = useState(true);
  const [deals, setDeals] = useState<DealWithStats[]>([]);
  const [filteredDeals, setFilteredDeals] = useState<DealWithStats[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDeals();
  }, []);

  useEffect(() => {
    filterDeals();
  }, [deals, searchQuery, statusFilter]);

  const loadDeals = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await adminService.listDeals();
      setDeals(result);
    } catch (err) {
      console.error('Failed to load deals:', err);
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  };

  const filterDeals = () => {
    let filtered = [...deals];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (deal) =>
          deal.name.toLowerCase().includes(query) ||
          deal.city.toLowerCase().includes(query) ||
          deal.address.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((deal) => (statusFilter === 'active' ? deal.isActive : !deal.isActive));
    }

    setFilteredDeals(filtered);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  const getPropertyTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      MULTIFAMILY: 'Multifamily',
      RETAIL: 'Retail',
      OFFICE: 'Office',
      INDUSTRIAL: 'Industrial',
      MIXED_USE: 'Mixed Use',
      HOTEL: 'Hotel',
      SELF_STORAGE: 'Self Storage',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  const getPropertyTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      MULTIFAMILY: 'bg-blue-100 text-blue-800',
      RETAIL: 'bg-purple-100 text-purple-800',
      OFFICE: 'bg-yellow-100 text-yellow-800',
      INDUSTRIAL: 'bg-gray-100 text-gray-800',
      MIXED_USE: 'bg-green-100 text-green-800',
      HOTEL: 'bg-pink-100 text-pink-800',
      SELF_STORAGE: 'bg-orange-100 text-orange-800',
      OTHER: 'bg-gray-100 text-gray-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('deals', 'view')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-12 bg-gray-200 rounded-lg" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Deals</h1>
          <p className="mt-1 text-sm text-gray-500">Manage investment properties and deals</p>
        </div>
        {canCreate && (
          <Button className="flex items-center gap-2" onClick={() => setShowCreateModal(true)}>
            <PlusIcon className="h-4 w-4" />
            Add Deal
          </Button>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <Card padding="md">
          <div className="flex items-center gap-3 text-red-700">
            <span className="text-sm">Error: {error}</span>
            <button onClick={loadDeals} className="text-sm font-medium underline hover:no-underline">
              Retry
            </button>
          </div>
        </Card>
      )}

      {/* Search & Filters */}
      <Card padding="md">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search deals..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="w-full md:w-40">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card padding="sm">
          <p className="text-sm text-gray-500">Total Deals</p>
          <p className="text-2xl font-bold text-gray-900">{deals.length}</p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Active Deals</p>
          <p className="text-2xl font-bold text-gray-900">{deals.filter((d) => d.isActive).length}</p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Total Equity Raised</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(deals.reduce((sum, d) => sum + d.totalEquityRaised, 0))}
          </p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Total Distributed</p>
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(deals.reduce((sum, d) => sum + d.totalDistributed, 0))}
          </p>
        </Card>
      </div>

      {/* Deals List */}
      <div className="space-y-4">
        {filteredDeals.map((deal) => (
          <Card key={deal.id} padding="md" hover>
            <Link to={`/admin/deals/${deal.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Deal Info */}
                <div className="flex items-start gap-4">
                  <div className="hidden sm:block w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                    <DealImage
                      dealId={deal.id}
                      dealName={deal.name}
                      imageUrl={deal.imageUrl}
                      className="w-full h-full object-cover"
                      fallbackClassName="w-full h-full flex items-center justify-center bg-primary-100"
                      iconClassName="h-8 w-8 text-primary-600"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold text-gray-900">{deal.name}</h3>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPropertyTypeColor(
                          deal.propertyType
                        )}`}
                      >
                        {getPropertyTypeLabel(deal.propertyType)}
                      </span>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          deal.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {deal.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                      <MapPinIcon className="h-4 w-4" />
                      {deal.address}, {deal.city}, {deal.state} {deal.zipCode}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Acquired: {formatDate(deal.acquisitionDate)}</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-6 text-right">
                  <div>
                    <p className="text-xs text-gray-500">Investors</p>
                    <div className="flex items-center justify-end gap-1">
                      <UsersIcon className="h-4 w-4 text-gray-400" />
                      <p className="text-sm font-semibold text-gray-900">{deal.investorCount}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Equity Raised</p>
                    <p className="text-sm font-semibold text-gray-900">{formatCurrency(deal.totalEquityRaised)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Distributed</p>
                    <p className="text-sm font-semibold text-green-600">{formatCurrency(deal.totalDistributed)}</p>
                  </div>
                </div>
              </div>

              {deal.description && <p className="mt-3 text-sm text-gray-500 line-clamp-2">{deal.description}</p>}
            </Link>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {filteredDeals.length === 0 && !error && (
        <Card padding="lg">
          <div className="text-center py-12">
            <BuildingOfficeIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No Deals Found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {searchQuery || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Add your first deal to get started'}
            </p>
            {!searchQuery && statusFilter === 'all' && canCreate && (
              <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Deal
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Create Deal Modal */}
      <CreateDealModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onCreated={loadDeals} />
    </div>
  );
}

export default DealsPage;
